/**
 * Auth hook — Sprint 2 F5.
 *
 * Reads auth state from localStorage (which is how V2 stored it).
 * Provides logout + current user/concessionaire info.
 */
import { useCallback, useEffect, useState } from 'react';
import egoalLogo from '../../../assets/images/logo/egoallogo.png';
import { suppressSessionExpiry } from './sessionExpired';

function readAuthFromStorage() {
    return {
        token: localStorage.getItem('sessiontoken') || '',
        refreshToken: localStorage.getItem('refresh_token') || '',
        userId: localStorage.getItem('loginId') || '',
        userRole: localStorage.getItem('usertype') || '',
        userName: localStorage.getItem('loginUsername') || '',
        firstName: localStorage.getItem('loginName') || '',
        concessionaireId: localStorage.getItem('concessionaireId') || '',
        concessionaireName: localStorage.getItem('concessionaireName') || '',
        propertyAccess: localStorage.getItem('propertyAccess') || '',
        isAuthenticated: !!localStorage.getItem('sessiontoken'),
    };
}

/**
 * Is an org-level module entitlement switched on?
 *
 * These mirror Concessionaire.features and are written at login from
 * GET /features/mine (see Auth/Signin loadOrgFeatures). Compared against '1'
 * rather than read for truthiness so an ABSENT key -- a session that predates
 * the flag, or a features probe that failed -- reads as OFF. The server gate is
 * fail-OPEN on an absent flag by design; the client must not be, or a stale
 * session would paint a module the client does not have.
 */
export function hasFeature(name) {
    return localStorage.getItem(name) === '1';
}

/*
 * The Digital Twin and its floor-plan importer.
 *
 * Gated ONLY on the concessionaire-level entitlement -- whether the client has
 * the module -- with no per-seat role test on top. Anyone signed in to an
 * entitled client gets it; access is granted and revoked in one place, the
 * concessionaire's own feature flags, rather than in two.
 *
 * Viewing and importing stay separate entitlements, because they are separate
 * rights: the importer rewrites the published plan that the map, the routing
 * and the unit list all read from, so a client can be entitled to read the
 * model without being able to redraw it.
 */
export const canViewDigitalTwin = () => hasFeature('featureDigitalTwin');
export const canImportFloorPlan = () => canViewDigitalTwin() && hasFeature('featureFloorPlanImport');

export function useAuth() {
    const [auth, setAuth] = useState(readAuthFromStorage);

    // Re-read when storage changes (e.g. after login)
    useEffect(() => {
        const handler = () => setAuth(readAuthFromStorage());
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    const logout = useCallback(() => {
        // Intentional sign-out — latch the session-expiry handler closed so the
        // 401s from clearing the token below don't pop the "Session has ended" alert.
        suppressSessionExpiry();

        // Show a minimal, branded loader during the sign-out transition (same
        // visual language as the Spatial loader) instead of a frozen page / flash.
        try {
            if (!document.querySelector('.v3-logout-loader')) {
                const o = document.createElement('div');
                o.className = 'v3-logout-loader';
                o.innerHTML =
                    '<img class="lo-img" src="' + egoalLogo + '" alt="EGoal" />' +
                    '<div class="lo-line"><div class="lo-fill"></div></div>' +
                    '<div class="lo-title"><strong>Signing out</strong> of EGoal</div>';
                document.body.appendChild(o);
            }
        } catch (_) { /* no-op */ }

        localStorage.clear();
        setAuth(readAuthFromStorage());
        // Hold the logo on screen for a beat (Gmail-style) before the hard nav.
        setTimeout(() => { window.location.href = `${process.env.PUBLIC_URL}/login`; }, 1600);
    }, []);

    return { ...auth, logout, refresh: () => setAuth(readAuthFromStorage()) };
}
